import React from 'react';
import { useTranslation } from 'react-i18next';

const categoryIcons = {
  authentication: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  ),
  content: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  ),
  default: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
};

const SCAM_TYPE_KEYS = [
  'phishing', 'fausse_convocation', 'arnaque_financiere', 'advance_fee',
  'lottery', 'sextortion', 'bec', 'invoice', 'delivery', 'crypto',
  'romance', 'tech_support', 'subscription', 'tax',
];

export default function SmartSummary({ summary, verdict, score }) {
  const { t, i18n } = useTranslation();
  if (!summary) return null;

  const isFr = i18n.language === 'fr';
  const tldr = isFr ? summary.tldr_fr : (summary.tldr || summary.tldr_fr);
  const recommendation = isFr ? summary.recommendation_fr : (summary.recommendation || summary.recommendation_fr);

  const verdictColors = {
    scam: { bg: 'bg-danger/10', border: 'border-danger/30', text: 'text-danger' },
    suspicious: { bg: 'bg-suspicious/10', border: 'border-suspicious/30', text: 'text-suspicious' },
    caution: { bg: 'bg-caution/10', border: 'border-caution/30', text: 'text-caution' },
    legitimate: { bg: 'bg-safe/10', border: 'border-safe/30', text: 'text-safe' },
  };
  const colors = verdictColors[verdict] || verdictColors.legitimate;

  return (
    <div className="space-y-6">
      {/* TLDR Banner */}
      {tldr && (
        <div className={`${colors.bg} ${colors.border} border rounded-xl p-5`}>
          <p className={`${colors.text} font-semibold text-lg`}>
            {tldr}
          </p>
          {summary.scam_type && (
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
              {t(`smartSummary.scamTypes.${summary.scam_type}`, summary.scam_type)}
            </span>
          )}
        </div>
      )}

      {/* Red Flags */}
      {summary.red_flags && summary.red_flags.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-danger mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            {t('smartSummary.criticalFlags')} ({summary.red_flags.length})
          </h3>
          <div className="space-y-3">
            {summary.red_flags.map((flag, i) => (
              <div key={i} className="flex gap-3 p-3 bg-danger/5 border border-danger/10 rounded-lg">
                <div className="text-danger flex-shrink-0 mt-0.5">
                  {categoryIcons[flag.category] || categoryIcons.default}
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-medium text-sm">{flag.title}</h4>
                  <p className="text-gray-400 text-sm mt-0.5">{flag.detail}</p>
                  {flag.matches && flag.matches.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {flag.matches.map((m, j) => (
                        <span key={j} className="bg-danger/10 text-danger text-xs px-2 py-0.5 rounded font-mono">
                          {m}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warnings */}
      {summary.warnings && summary.warnings.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-caution mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('smartSummary.warnings')} ({summary.warnings.length})
          </h3>
          <div className="space-y-2">
            {summary.warnings.map((w, i) => (
              <div key={i} className="flex gap-3 p-3 bg-caution/5 border border-caution/10 rounded-lg">
                <div className="text-caution flex-shrink-0 mt-0.5">
                  {categoryIcons[w.category] || categoryIcons.default}
                </div>
                <div>
                  <h4 className="text-white font-medium text-sm">{w.title}</h4>
                  <p className="text-gray-400 text-sm mt-0.5">{w.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Positive Signals */}
      {summary.positive_signals && summary.positive_signals.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-safe mb-3">{t('smartSummary.positiveSignals')}</h3>
          {summary.positive_signals.map((p, i) => (
            <div key={i} className="flex gap-2 items-center text-sm text-gray-300 mb-1">
              <svg className="w-4 h-4 text-safe" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>{p.title}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recommendation */}
      {recommendation && (
        <div className="glass-card p-6 border-l-4 border-safe/50">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <svg className="w-5 h-5 text-safe" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.746 3.746 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
            </svg>
            {t('smartSummary.whatToDo')}
          </h3>
          <div className="text-gray-300 text-sm space-y-1 whitespace-pre-line">
            {recommendation}
          </div>
        </div>
      )}
    </div>
  );
}
